import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { styled } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import { Info } from '@material-ui/icons';

import { SdmTypography } from 'components';
import { theme } from 'theme';
import CustomRadioButton from './styledComponents/CustomRadioButton';
import CustomExpandIcon from './styledComponents/CustomExpandIcon';
import DropZonePreviewImage from './styledComponents/DropZonePreviewImage';
import BoxContainer from './styledComponents/BoxContainer';
import translations from 'utils/translations';
import {
  AccordionNameEnum,
  AutocompleteEnum,
  handleDropAfterErrors,
  PathInfoImages,
  useEntities,
} from 'utils/drupalEntities';

import RHFAutocomplete from './styledComponents/RHFAutocomplete';
import CustomTextFieldComponent from './styledComponents/CustomTextFieldComponent';
import LabelContainer from './additionalComponents/LabelContainer';
import RHFApiAutocomplete from './styledComponents/RHFApiAutocomplete';
import { backHomeLink } from 'utils/constants';

const AccordionSummaryContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const AccordionContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

const GeneralAccordeon = ({
  setValue,
  errors,
  control,
  watch,
  setCustomErrors,
  generalData,
  langCode,
  isBrand,
  productLangCode,
  isOpen = false,
  setInfoDialog,
}) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const { getViews } = useEntities();
  // States
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [components, setComponents] = useState(false);
  const [subCategoriesOptions, setSubCategoriesOptions] = useState();
  const [categoriesOptions, setCategoriesOptions] = useState();
  // Watchers and Refs
  const wProductClasses = watch('field_classe_merceologica');
  const wProductSub = watch('field_sotto_categoria');

  // Constants
  const titleColor = isAccordionOpen
    ? theme.colors.primary.red
    : theme.colors.primary.black;
  const iconColor = isAccordionOpen ? 'primaryOpen' : 'primaryClosed';
  const isEn = productLangCode === 'en';
  const isNotTranslatable = (key) => isEn && !components?.[key]?.isTranslatable;

  const isNotImgTranslatable = (key) =>
    isEn && !components?.[key]?.isImgTranslatable;
  const isNotAltTranslatable = (key) =>
    isEn && !components?.[key]?.isAltTranslatable;

  const emptySub = () => {
    if (components?.field_sotto_categoria?.name) {
      setSubCategoriesOptions([]);
      setValue(components?.field_sotto_categoria?.name, null);
    }
  };
  const emptyCat = () => {
    if (components?.field_categoria?.name) {
      setCategoriesOptions([]);
      setValue(components?.field_categoria?.name, []);
    }
  };

  // Effects
  useEffect(() => {
    if (generalData?.length) {
      const _components = {};
      generalData.map((comp) => (_components[comp.name] = comp));
      _components && setComponents(_components);
    }
  }, [generalData]);

  useEffect(() => {
    const fetchSubCategories = async (prodClass) => {
      emptySub();
      const res = await getViews({
        useLoader: true,
        entity: AutocompleteEnum.productSubCategories,
        bundle: 'field_s_classe_merceologica_target_id',
        bundleId: prodClass.value,
        langCode: langCode,
      });
      const _productSubCategoriesRes = res?.data || [];
      const _productSubCategoriesOptions = _productSubCategoriesRes.map(
        (p) => ({
          value: p?.attributes?.drupal_internal__tid || '',
          label: p?.attributes?.name || '',
          entityId: p?.id,
        })
      );
      setSubCategoriesOptions(_productSubCategoriesOptions);
    };
    if (wProductClasses?.value) {
      fetchSubCategories(wProductClasses);
    } else {
      emptySub();
    }
  }, [wProductClasses]);

  useEffect(() => {
    const fetchCategories = async (prodClass) => {
      emptyCat();
      const res = await getViews({
        useLoader: true,
        entity: AutocompleteEnum.productCategories,
        bundle: 'field_sotto_categoria_target_id',
        bundleId: prodClass.value,
        langCode: langCode,
      });
      const _productCategoriesRes = res?.data || [];
      const _productCategoriesOptions = _productCategoriesRes.map((p) => ({
        value: p?.attributes?.drupal_internal__tid || '',
        label: p?.attributes?.name || '',
        entityId: p?.id,
      }));
      setCategoriesOptions(_productCategoriesOptions);
    };
    if (wProductSub?.value) {
      fetchCategories(wProductSub);
    } else {
      emptyCat();
    }
  }, [wProductSub]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsAccordionOpen(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Functions
  const handleChange = (acceptedFiles, context) => {
    const c = components[context];
    handleDropAfterErrors({
      files: acceptedFiles,
      fieldName: c.name,
      maxResolution: c.maxResolution,
      maxSize: c.maxSize,
      fileExtension: c.extensions.split(' '),
      setCustomErrors,
      setValue,
      i18nErrors: i18n.errors,
    });
  };

  const renderNotTranslatableMsg = (key) =>
    isNotTranslatable(key) ? (
      <SdmTypography
        variant={'h5'}
        style={{ margin: 0, color: theme.colors.primary.red }}
      >
        {i18n.tooltip.errorTranslate}
      </SdmTypography>
    ) : null;

  const handleInfo = (key, title) =>
    setInfoDialog({
      i18n: { title },
      isOpen: true,
      img: PathInfoImages(langCode)?.[AccordionNameEnum.GENERAL]?.[key],
    });

  const hasLinkFields = (components) =>
    components.field_link_facebook ||
    components.field_link_instagram ||
    components.field_link_linkedin ||
    components.field_link_pinterest ||
    components.field_link_twitter;

  const possibleSocialLinks = [
    'field_link_facebook',
    'field_link_instagram',
    'field_link_linkedin',
    'field_link_pinterest',
    'field_link_twitter',
  ];

  return (
    <Accordion
      onChange={() => setIsAccordionOpen(!isAccordionOpen)}
      expanded={isAccordionOpen}
    >
      <AccordionSummary expandIcon={<CustomExpandIcon variant={iconColor} />}>
        <AccordionSummaryContent>
          <SdmTypography
            variant={'h3'}
            style={{
              color: titleColor,
              margin: 0,
            }}
          >
            {i18n.titles.generalAccordeon}
          </SdmTypography>
          <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
            {
              i18n.titles[
                isBrand ? 'generalAccordeonSubBrand' : 'generalAccordeonSub'
              ]
            }
          </SdmTypography>
        </AccordionSummaryContent>
      </AccordionSummary>

      <AccordionDetails>
        {components && (
          <AccordionContent>
            {components.field_logo && (
              <BoxContainer>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <SdmTypography
                    variant={'h5'}
                    style={{ margin: 0, textTransform: 'uppercase' }}
                  >
                    {components.field_logo.label}
                    {components.field_logo.isRequired ? (
                      <span
                        style={{
                          color: theme.colors.primary.red,
                          fontWeight: 400,
                        }}
                      >
                        {' '}
                        *
                      </span>
                    ) : (
                      ''
                    )}
                  </SdmTypography>
                  <Info
                    style={{
                      height: '25px',
                      color: theme.colors.primary.grey,
                      marginBottom: 5,
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      handleInfo(
                        components?.field_logo?.name,
                        components.field_logo.label
                      )
                    }
                  />
                </div>

                <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
                  {components.field_logo.description}
                </SdmTypography>
                {renderNotTranslatableMsg('field_logo')}

                <DropZonePreviewImage
                  error={errors?.document}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  disableImg={isNotImgTranslatable('field_logo')}
                  disableAlt={isNotAltTranslatable('field_logo')}
                  name={components?.field_logo?.name}
                  altName={`alt_${components?.field_logo?.name}_0`}
                  altDescription={isBrand ? i18n.text.altDescription : ''}
                  accept={{
                    'image/*': components.field_logo.extensions
                      ? components.field_logo.extensions
                          .split(' ')
                          .map((e) => `.${e}`)
                      : ['jpg'],
                  }}
                  handleChange={(files) => handleChange(files, 'field_logo')}
                  isMultiple={false}
                  specs={[
                    i18n.imageSpecs.number,
                    `${i18n.imageSpecs.size} ${components.field_logo.maxSize}`,
                    `${i18n.imageSpecs.format} ${components.field_logo.extensions}`,
                    `${i18n.imageSpecs.logoSpecSize} ${components.field_logo.maxResolution}px`,
                  ]}
                  maxResolution={components.field_logo.maxResolution}
                  setCustomErrors={setCustomErrors}
                />
              </BoxContainer>
            )}
            {components.field_immagine_anteprima && (
              <BoxContainer>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <SdmTypography
                    variant={'h5'}
                    style={{ margin: 0, textTransform: 'uppercase' }}
                  >
                    {components.field_immagine_anteprima.label}
                    {components.field_immagine_anteprima.isRequired ? (
                      <span
                        style={{
                          color: theme.colors.primary.red,
                          fontWeight: 400,
                        }}
                      >
                        {' '}
                        *
                      </span>
                    ) : (
                      ''
                    )}
                  </SdmTypography>
                  <Info
                    style={{
                      height: '25px',
                      marginBottom: 5,
                      color: theme.colors.primary.grey,
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      handleInfo(
                        components?.field_immagine_anteprima?.name,
                        components.field_immagine_anteprima.label
                      )
                    }
                  />
                </div>
                <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
                  {components.field_immagine_anteprima.description}
                </SdmTypography>
                {renderNotTranslatableMsg('field_immagine_anteprima')}
                <DropZonePreviewImage
                  error={errors?.document}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  disableImg={isNotImgTranslatable('field_immagine_anteprima')}
                  disableAlt={isNotAltTranslatable('field_immagine_anteprima')}
                  name={components?.field_immagine_anteprima?.name}
                  altName={`alt_${components?.field_immagine_anteprima?.name}_0`}
                  accept={{
                    'image/*': components.field_immagine_anteprima.extensions
                      ? components.field_immagine_anteprima.extensions
                          .split(' ')
                          .map((e) => `.${e}`)
                      : ['jpg'],
                  }}
                  handleChange={(files) =>
                    handleChange(files, 'field_immagine_anteprima')
                  }
                  isMultiple={false}
                  specs={[
                    i18n.imageSpecs.number,
                    `${i18n.imageSpecs.size} ${components.field_immagine_anteprima.maxSize}`,
                    `${i18n.imageSpecs.format} ${components.field_immagine_anteprima.extensions}`,
                    `${i18n.imageSpecs.logoSpecSize} ${components.field_immagine_anteprima.maxResolution}px`,
                  ]}
                  maxResolution={
                    components.field_immagine_anteprima.maxResolution
                  }
                  setCustomErrors={setCustomErrors}
                />
              </BoxContainer>
            )}

            {components.field_email_di_contatto ||
            components.field_email_di_contatto_estero ? (
              <BoxContainer>
                {components.field_email_di_contatto ? (
                  <>
                    <SdmTypography
                      variant={'h5'}
                      style={{ margin: 0, textTransform: 'uppercase' }}
                    >
                      {components.field_email_di_contatto.label}
                      {components.field_email_di_contatto.isRequired ? (
                        <span
                          style={{
                            color: theme.colors.primary.red,
                            fontWeight: 400,
                          }}
                        >
                          {' '}
                          *
                        </span>
                      ) : (
                        ''
                      )}
                    </SdmTypography>
                    <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
                      {components.field_email_di_contatto.description}
                    </SdmTypography>
                    {renderNotTranslatableMsg('field_email_di_contatto')}
                    <Controller
                      name={components.field_email_di_contatto.name}
                      control={control}
                      defaultValue=""
                      render={(props) => {
                        return (
                          <CustomTextFieldComponent
                            label={components.field_email_di_contatto.label}
                            placeholder={i18n.inputText.email}
                            variant="outlined"
                            onChange={(e) =>
                              props.onChange(e.target.value.trim())
                            }
                            value={props.value}
                            disabled={isNotTranslatable(
                              'field_email_di_contatto'
                            )}
                          />
                        );
                      }}
                    />
                  </>
                ) : null}
                {components.field_email_di_contatto_estero ? (
                  <>
                    <>
                      <SdmTypography
                        variant={'h5'}
                        style={{ margin: 0, textTransform: 'uppercase' }}
                      >
                        {components.field_email_di_contatto_estero.label}
                        {components.field_email_di_contatto_estero
                          .isRequired ? (
                          <span
                            style={{
                              color: theme.colors.primary.red,
                              fontWeight: 400,
                            }}
                          >
                            {' '}
                            *
                          </span>
                        ) : (
                          ''
                        )}
                      </SdmTypography>
                      <SdmTypography
                        variant={'h5Regular'}
                        style={{ margin: 0 }}
                      >
                        {components.field_email_di_contatto_estero.description}
                      </SdmTypography>
                      {renderNotTranslatableMsg(
                        'field_email_di_contatto_estero'
                      )}
                      <Controller
                        name={components.field_email_di_contatto_estero.name}
                        control={control}
                        defaultValue=""
                        render={(props) => {
                          return (
                            <CustomTextFieldComponent
                              label={
                                components.field_email_di_contatto_estero.label
                              }
                              placeholder={i18n.inputText.foreignEmail}
                              variant="outlined"
                              onChange={(e) =>
                                props.onChange(e.target.value.trim())
                              }
                              value={props.value}
                              disabled={isNotTranslatable(
                                'field_email_di_contatto_estero'
                              )}
                            />
                          );
                        }}
                      />
                    </>
                  </>
                ) : null}
              </BoxContainer>
            ) : null}

            {/* Adding Products Link */}
            {isBrand && hasLinkFields(components) ? (
              <BoxContainer>
                {possibleSocialLinks.map((linkName) =>
                  components[linkName] ? (
                    <>
                      <SdmTypography
                        variant={'h5'}
                        style={{ margin: 0, textTransform: 'uppercase' }}
                      >
                        {components[linkName].label}
                        {components[linkName].isRequired ? (
                          <span
                            style={{
                              color: theme.colors.primary.red,
                              fontWeight: 400,
                            }}
                          >
                            {' '}
                            *
                          </span>
                        ) : (
                          ''
                        )}
                      </SdmTypography>
                      <SdmTypography
                        variant={'h5Regular'}
                        style={{ margin: 0 }}
                      >
                        {components[linkName].description}
                      </SdmTypography>
                      {renderNotTranslatableMsg(linkName)}
                      <Controller
                        name={components[linkName].name}
                        control={control}
                        defaultValue=""
                        render={(props) => {
                          return (
                            <CustomTextFieldComponent
                              label={components[linkName].label}
                              placeholder={
                                components[linkName].placeholder_title ||
                                components[linkName].label
                              }
                              variant="outlined"
                              onChange={(e) => props.onChange(e.target.value)}
                              value={props.value}
                              disabled={isNotTranslatable(linkName)}
                            />
                          );
                        }}
                      />
                    </>
                  ) : null
                )}
              </BoxContainer>
            ) : null}

            {components.field_prodotto_nuovo && (
              <BoxContainer>
                <Controller
                  name={components.field_prodotto_nuovo.name}
                  control={control}
                  defaultValue={'0'}
                  render={(props) => (
                    <CustomRadioButton
                      title={components.field_prodotto_nuovo?.label}
                      isRequired={components.field_prodotto_nuovo.isRequired}
                      description={`${
                        components.field_prodotto_nuovo.description ||
                        i18n.titles.new
                      }`}
                      notTranslatableRender={renderNotTranslatableMsg(
                        'field_prodotto_nuovo'
                      )}
                      options={components.field_prodotto_nuovo.options}
                      footer={
                        components.field_prodotto_nuovo.isRequired &&
                        i18n.text.mandatoryInformation
                      }
                      value={props.value}
                      onChange={(e) => {
                        props.onChange(e.target.value);
                      }}
                      disabled={isNotTranslatable('field_prodotto_nuovo')}
                    />
                  )}
                />
              </BoxContainer>
            )}

            {components.field_prodotto_anteprima && (
              <BoxContainer>
                <Controller
                  name={components.field_prodotto_anteprima.name}
                  control={control}
                  defaultValue={'0'}
                  render={(props) => {
                    return (
                      <CustomRadioButton
                        title={components.field_prodotto_anteprima?.label}
                        isRequired={
                          components.field_prodotto_anteprima.isRequired
                        }
                        description={`${
                          components.field_prodotto_anteprima.description ||
                          i18n.titles.previewFlag
                        }`}
                        notTranslatableRender={renderNotTranslatableMsg(
                          'field_prodotto_anteprima'
                        )}
                        options={components.field_prodotto_anteprima.options}
                        footer={
                          components.field_prodotto_anteprima.isRequired &&
                          i18n.text.mandatoryInformation
                        }
                        value={props.value}
                        onChange={(e) => {
                          props.onChange(e.target.value);
                        }}
                        disabled={isNotTranslatable('field_prodotto_anteprima')}
                      />
                    );
                  }}
                />
              </BoxContainer>
            )}

            {components.field_prodotto_esposto && (
              <BoxContainer>
                <Controller
                  name={components.field_prodotto_esposto.name}
                  control={control}
                  defaultValue={'0'}
                  render={(props) => {
                    return (
                      <CustomRadioButton
                        title={components.field_prodotto_esposto?.label}
                        isRequired={
                          components.field_prodotto_esposto.isRequired
                        }
                        description={`${
                          components.field_prodotto_esposto.description ||
                          i18n.titles.exposed
                        }`}
                        notTranslatableRender={renderNotTranslatableMsg(
                          'field_prodotto_esposto'
                        )}
                        options={components.field_prodotto_esposto.options}
                        footer={
                          components.field_prodotto_esposto.isRequired &&
                          i18n.text.mandatoryInformation
                        }
                        value={props.value}
                        onChange={(e) => {
                          props.onChange(e.target.value);
                        }}
                        disabled={isNotTranslatable('field_prodotto_esposto')}
                      />
                    );
                  }}
                />
              </BoxContainer>
            )}

            {components.field_unboxing_2024 && (
              <BoxContainer>
                <Controller
                  name={components.field_unboxing_2024.name}
                  control={control}
                  defaultValue={'0'}
                  render={(props) => {
                    return (
                      <CustomRadioButton
                        title={components.field_unboxing_2024?.label}
                        isRequired={components.field_unboxing_2024.isRequired}
                        description={`${components.field_unboxing_2024.description}`}
                        notTranslatableRender={renderNotTranslatableMsg(
                          'field_unboxing_2024'
                        )}
                        options={components.field_unboxing_2024.options}
                        footer={
                          components.field_unboxing_2024.isRequired &&
                          i18n.text.mandatoryInformation
                        }
                        value={props.value}
                        onChange={(e) => {
                          props.onChange(e.target.value);
                        }}
                        disabled={isNotTranslatable('field_unboxing_2024')}
                      />
                    );
                  }}
                />
              </BoxContainer>
            )}

            {components.field_classe_merceologica && (
              <BoxContainer>
                <SdmTypography
                  variant={'h5'}
                  style={{ margin: 0, textTransform: 'uppercase' }}
                >
                  {i18n.titles.category}
                </SdmTypography>
                <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
                  {i18n.titles.categorySub}
                </SdmTypography>
                {renderNotTranslatableMsg('field_classe_merceologica')}
                <RHFAutocomplete
                  options={components.field_classe_merceologica.options}
                  label={`${components.field_classe_merceologica.label} ${
                    components.field_classe_merceologica.isRequired ? '*' : ''
                  }`}
                  name={components.field_classe_merceologica.name}
                  control={control}
                  disabled={isNotTranslatable('field_classe_merceologica')}
                />
                {renderNotTranslatableMsg('field_sotto_categoria')}
                <RHFAutocomplete
                  options={subCategoriesOptions}
                  label={`${components.field_sotto_categoria.label} ${
                    components.field_sotto_categoria.isRequired ? '*' : ''
                  }`}
                  name={components.field_sotto_categoria.name}
                  control={control}
                  disabled={isNotTranslatable('field_sotto_categoria')}
                />
                {renderNotTranslatableMsg('field_categoria')}
                <RHFAutocomplete
                  options={categoriesOptions}
                  label={`${components.field_categoria.label} ${
                    components.field_categoria.isRequired ? '*' : ''
                  }`}
                  name={components.field_categoria.name}
                  control={control}
                  multiple={true}
                  disabled={isNotTranslatable('field_categoria')}
                />
              </BoxContainer>
            )}

            {components.field_private_collezione && (
              <BoxContainer>
                <LabelContainer
                  label={components.field_private_collezione.label}
                  isRequired={components.field_private_collezione.isRequired}
                  description={components.field_private_collezione.description}
                />
                {renderNotTranslatableMsg('field_private_collezione')}
                <RHFApiAutocomplete
                  control={control}
                  name={components.field_private_collezione.name}
                  placeholder={
                    components.field_private_collezione.placeholder ||
                    components.field_private_collezione.label
                  }
                  label={components.field_private_collezione.label}
                  endpoint={`${backHomeLink}/${langCode}/jsonapi/${components.field_private_collezione.formEntityType}/${components.field_private_collezione.formEntityBundle}`}
                  endpointEntity={'name'}
                  disabled={isNotTranslatable('field_private_collezione')}
                  freeSolo
                />
              </BoxContainer>
            )}
          </AccordionContent>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default GeneralAccordeon;
